import {
	isRouteErrorResponse,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
} from "react-router";

import type { Route } from "./+types/root";
import "./app.css";
import { initSentry, Sentry } from "~/lib/sentry";

initSentry();

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { Toaster } from "sonner";
import { SidebarProvider } from "~/components/layout/sidebar-context";
import { AuthProvider } from "~/lib/hooks/use-auth";

export const links: Route.LinksFunction = () => [
	{ rel: "icon", type: "image/png", sizes: "96x96", href: "/favicon-96x96.png" },
	{ rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
	{ rel: "preconnect", href: "https://fonts.googleapis.com" },
	{
		rel: "preconnect",
		href: "https://fonts.gstatic.com",
		crossOrigin: "anonymous",
	},
	{
		rel: "stylesheet",
		href: "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400..700&family=Manrope:wght@400..800&display=swap",
	},
];

export function Layout({ children }: { children: React.ReactNode }) {
	return (
		<html lang="en">
			<head>
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<Meta />
				<Links />
			</head>
			<body>
				{children}
				<Toaster />
				<ScrollRestoration />
				<Scripts />
			</body>
		</html>
	);
}

const queryClient = new QueryClient();

export default function App() {
	return (
		<AuthProvider>
			<QueryClientProvider client={queryClient}>
				<SidebarProvider>
					<Outlet />
				</SidebarProvider>
			</QueryClientProvider>
		</AuthProvider>
	);
}

export function HydrateFallback() {
	return (
		<div className="min-h-screen flex items-center justify-center">
			<div className="loading loading-spinner loading-lg" />
		</div>
	);
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
	// Report non-404 errors to Sentry
	if (!isRouteErrorResponse(error)) {
		Sentry.captureException(error);
	}

	let message = "Oops!";
	let details = "An unexpected error occurred.";
	let stack: string | undefined;

	if (isRouteErrorResponse(error)) {
		message = error.status === 404 ? "404" : "Error";
		details =
			error.status === 404 ? "The requested page could not be found." : error.statusText || details;
	} else if (import.meta.env.DEV && error && error instanceof Error) {
		details = error.message;
		stack = error.stack;
	}

	return (
		<main className="min-h-screen flex flex-col items-center justify-center p-4">
			<h1 className="text-2xl font-bold">{message}</h1>
			<p className="mt-2 text-muted-foreground">{details}</p>
			{stack && (
				<pre className="mt-4 w-full max-w-2xl p-4 overflow-x-auto text-xs bg-muted rounded">
					<code>{stack}</code>
				</pre>
			)}
			<a href="/login" className="mt-6 btn btn-primary">
				Go to login
			</a>
		</main>
	);
}
